import InputField from '../../../components/Forms/Input/InputField';
import { mapPosSettings, toPosUpdateDTO } from './RestaurantSettingsMapper';
import RestaurantSettingsFormLayout from './RestaurantSettingsFormLayout';
import RestaurantSettingsToggle from './RestaurantSettingsToggle';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import useRestaurantSettingsForm from './useRestaurantSettingsForm';
import { getServiceChargePercentageError } from './restaurantPosSettingsValidation';

interface Props {
  branchId: number;
}

const RestaurantPosSettingsForm = ({ branchId }: Props) => {
  const settings = useRestaurantSettingsForm(
    branchId,
    'pos',
    mapPosSettings,
    toPosUpdateDTO
  );
  const { form, updateField } = settings;
  const serviceChargeError = form
    ? getServiceChargePercentageError(
        form.serviceChargeEnabled,
        form.serviceChargePercentage
      )
    : undefined;

  const save = () => {
    if (serviceChargeError) {
      settings.setAlert({
        type: 'error',
        title: 'Porcentaje inválido',
        message: serviceChargeError,
      });
      return;
    }

    void settings.save();
  };

  return (
    <RestaurantSettingsFormLayout
      title="Punto de venta"
      description="Reglas para cuentas, servicio y administración de órdenes y mesas."
      loading={settings.loading}
      saving={settings.saving}
      hasData={Boolean(form)}
      alert={settings.alert}
      onCloseAlert={() => settings.setAlert(null)}
      onSave={save}
      onRetry={settings.reload}
      help={<RestaurantFormHelp topic="settingsPos" variant="inline" />}
    >
      {form && (
        <>
          <RestaurantSettingsToggle
            label="Dividir cuenta"
            description="Permite separar una orden en varias cuentas al cobrar."
            name="split-bill"
            checked={form.splitBillEnabled}
            onChange={() =>
              updateField('splitBillEnabled', !form.splitBillEnabled)
            }
          />
          <RestaurantSettingsToggle
            label="Activar propina o cargo por servicio"
            description="El porcentaje se aplicará únicamente a los productos marcados como “Aplica propina”."
            name="service-charge"
            checked={form.serviceChargeEnabled}
            onChange={() =>
              updateField('serviceChargeEnabled', !form.serviceChargeEnabled)
            }
          />
          <InputField
            label="Porcentaje de propina o servicio"
            name="serviceChargePercentage"
            type="number"
            iconKey="percentage"
            min={0.01}
            max={100}
            step="0.01"
            value={form.serviceChargePercentage}
            disabled={!form.serviceChargeEnabled}
            error={serviceChargeError}
            onChange={(event) =>
              updateField('serviceChargePercentage', Number(event.target.value))
            }
            rightContent="%"
          />
          <RestaurantSettingsToggle
            label="Permitir reabrir órdenes"
            description="Autoriza volver a trabajar con órdenes previamente cerradas."
            name="allow-reopen-order"
            checked={form.allowReopenOrder}
            onChange={() =>
              updateField('allowReopenOrder', !form.allowReopenOrder)
            }
          />
          <RestaurantSettingsToggle
            label="Permitir mover mesas"
            description="Permite trasladar una orden activa a otra mesa."
            name="allow-table-transfer"
            checked={form.allowTableTransfer}
            onChange={() =>
              updateField('allowTableTransfer', !form.allowTableTransfer)
            }
          />
          <RestaurantSettingsToggle
            label="Permitir fusionar órdenes"
            description="Permite combinar dos o más órdenes abiertas."
            name="allow-merge-orders"
            checked={form.allowMergeOrders}
            onChange={() =>
              updateField('allowMergeOrders', !form.allowMergeOrders)
            }
          />
          <RestaurantSettingsToggle
            label="Mover productos entre órdenes"
            description="Permite transferir productos individuales entre órdenes."
            name="allow-move-order-items"
            checked={form.allowMoveOrderItems}
            onChange={() =>
              updateField('allowMoveOrderItems', !form.allowMoveOrderItems)
            }
          />
        </>
      )}
    </RestaurantSettingsFormLayout>
  );
};

export default RestaurantPosSettingsForm;
